import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, Clock, ShieldCheck, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import {
  deriveLocalRollup,
  ROLLUP_LABELS,
  usePatchVerificationMutation,
  useVerificationQuery,
  type AveroVerificationValue,
  type RollupColor,
  type VendorClaimValue,
  type VerificationAxis,
  type VerificationDetail,
} from '@/lib/api/verification';
import { cn } from '@/lib/utils/cn';

/**
 * T-0115 / E10 — Dual-axis verification setter modal.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-verify.js`
 * → `openVerifyModal(recordKey)` + `design_handoff_caliber_v2/README.md`
 * §4 modal `data-set-verify`, §8 pattern 2 ("dual-axis: never collapse
 * vendor_claim + avero_verification into one status").
 *
 * What this modal does:
 *   - Loads the existing axes via T-0037's GET
 *     `/registers/:type/:id/verification`.
 *   - Lets the user move EITHER axis independently — vendor_claim and
 *     avero_verification are presented as parallel cards, never as a
 *     single "set status" picker (D-21 design invariant).
 *   - Each axis is committed via a separate PATCH so the audit row
 *     records the move with its own actor + timestamp + structured tag.
 *   - Shows a local rollup preview using `deriveLocalRollup` — the
 *     backend recomputes the canonical rollup on save (this preview is
 *     for confidence; the saved value comes from the API response).
 *
 * Why we DO NOT collapse: the prototype's `verify-axes-pair` UI puts the
 * vendor card on the left and the Avero card on the right with the
 * options + chips visually separate. Per README §8 pattern 2 ("axes are
 * orthogonal — never collapse"), even when both values match the modal
 * keeps the two cards distinct so the consultant can move one without
 * touching the other.
 *
 * Per ADR-008 / T-0037 §2: this surface enforces that vendor-source
 * documents can only move `vendor_claim` and Avero-source documents
 * (or no evidence at all) can move `avero_verification`. The evidence
 * picker is a future v2 (T-0037 §5 — currently the consultant-judgment
 * path is the v1 surface for the Avero axis; vendor axis writes use the
 * existing register PATCH which doesn't require an evidence link).
 */

export interface VerifyStateModalProps {
  engagementId: string;
  registerType: string;
  /** Register entry id (deliverable, criterion, checkpoint, domain). */
  entryId: string;
  /**
   * Human-readable subject name (used in modal title). Caller passes
   * what they have — the modal renders metadata from the entry row.
   */
  subjectName: string;
  /** Optional kind label — "Deliverable", "Readiness criterion", etc. */
  subjectKind?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const VENDOR_CLAIM_VALUES: { value: VendorClaimValue; label: string; icon: typeof Check }[] = [
  { value: 'pass', label: 'Pass', icon: Check },
  { value: 'fail', label: 'Fail', icon: X },
  { value: 'pending', label: 'Pending', icon: Clock },
  { value: 'na', label: 'N/A', icon: Clock },
];

const AVERO_VALUES: {
  value: AveroVerificationValue;
  label: string;
  icon: typeof Check;
}[] = [
  { value: 'pass', label: 'Pass', icon: Check },
  { value: 'fail', label: 'Fail', icon: X },
  { value: 'in_progress', label: 'In progress', icon: Clock },
  { value: 'unverified', label: 'Unverified', icon: Clock },
];

export function VerifyStateModal({
  engagementId,
  registerType,
  entryId,
  subjectName,
  subjectKind,
  open,
  onOpenChange,
}: VerifyStateModalProps) {
  const verification = useVerificationQuery(engagementId, registerType, entryId, {
    enabled: open,
  });
  const patch = usePatchVerificationMutation(engagementId, registerType, entryId);

  // Pending axis writes — these are the local working state for the
  // axis the user is about to commit. Each axis tracks its own draft so
  // the user can stage both axes before saving.
  const [draftVendor, setDraftVendor] = useState<VendorClaimValue | null>(null);
  const [draftAvero, setDraftAvero] = useState<AveroVerificationValue | null>(null);
  const [savingAxis, setSavingAxis] = useState<VerificationAxis | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);

  // Reset draft state on close.
  useEffect(() => {
    if (!open) {
      setDraftVendor(null);
      setDraftAvero(null);
      setSavingAxis(null);
      setSaveError(null);
    }
  }, [open]);

  const data = verification.data;
  // Use the draft when set, otherwise fall back to the persisted value.
  const effectiveVendor = draftVendor ?? data?.vendor_claim ?? null;
  const effectiveAvero = draftAvero ?? data?.avero_verification ?? null;
  const previewRollup: RollupColor | null =
    effectiveVendor && effectiveAvero ? deriveLocalRollup(effectiveVendor, effectiveAvero) : null;

  async function commitAxis(axis: VerificationAxis): Promise<void> {
    const value = axis === 'vendor_claim' ? draftVendor : draftAvero;
    if (!value) return;
    setSavingAxis(axis);
    setSaveError(null);
    try {
      await patch.mutateAsync({
        input: { axis, value },
        idempotencyKey:
          typeof globalThis.crypto?.randomUUID === 'function'
            ? globalThis.crypto.randomUUID()
            : `verify-${axis}-${Date.now()}`,
      });
      // Clear only the axis we just saved — the other axis's draft (if
      // any) stays so the consultant can stage both axes in sequence.
      if (axis === 'vendor_claim') setDraftVendor(null);
      else setDraftAvero(null);
    } catch (err) {
      setSaveError(err);
    } finally {
      setSavingAxis(null);
    }
  }

  const hasUnsavedDraft = draftVendor !== null || draftAvero !== null;

  function requestClose(next: boolean) {
    if (!next && hasUnsavedDraft) {
      const ok =
        typeof window !== 'undefined' && typeof window.confirm === 'function'
          ? window.confirm('Discard the un-saved axis change?')
          : true;
      if (!ok) return;
    }
    onOpenChange(next);
  }

  const titleId = `verify-modal-title-${entryId}`;

  return (
    <Dialog.Root open={open} onOpenChange={requestClose}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-[min(820px,92vw)] max-h-[88vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-ink-100 px-6 py-4 pr-14">
            <div className="min-w-0 flex items-start gap-3">
              <div
                aria-hidden="true"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-card bg-ai-surface text-ai-ink"
              >
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-meta uppercase tracking-wide text-ink-500">
                  {subjectKind ?? 'Register entry'} · Set Avero verification
                </div>
                <Dialog.Title
                  id={titleId}
                  className="mt-1 truncate text-h2 font-semibold text-ink-900"
                  title={subjectName}
                >
                  {subjectName}
                </Dialog.Title>
                <div className="mt-1 text-meta text-ink-500">
                  Axes are orthogonal — each write is audited independently (D-21 gate)
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-y-auto px-6 py-5">
            {verification.isLoading ? (
              <div className="flex items-center gap-2 text-body-sm text-ink-500">
                <Spinner /> Loading verification state…
              </div>
            ) : verification.error ? (
              <ErrorMessage error={verification.error} />
            ) : !data ? (
              <p className="text-body-sm text-ink-500">No verification data.</p>
            ) : (
              <div className="space-y-5">
                <div className="grid gap-3 md:grid-cols-2">
                  <AxisCard
                    title="Vendor claim"
                    badge="V"
                    note="read-only on the Avero axis · vendor-source evidence only"
                    persisted={data.vendor_claim}
                    draft={draftVendor}
                    options={VENDOR_CLAIM_VALUES}
                    onPick={(v) => setDraftVendor(v === data.vendor_claim ? null : v)}
                    saving={savingAxis === 'vendor_claim'}
                  />
                  <AxisCard
                    title="Avero verification"
                    badge="A"
                    note="consultant-authoritative · Avero-source evidence OR judgment"
                    persisted={data.avero_verification}
                    draft={draftAvero}
                    options={AVERO_VALUES}
                    onPick={(v) => setDraftAvero(v === data.avero_verification ? null : v)}
                    saving={savingAxis === 'avero_verification'}
                  />
                </div>

                <RollupPreview
                  current={data.rollup_color}
                  previewed={previewRollup}
                  staged={hasUnsavedDraft}
                />

                {saveError ? <ErrorMessage error={saveError} /> : null}

                <AxisCommitRow
                  axis="vendor_claim"
                  label="Save vendor claim"
                  hasDraft={draftVendor !== null}
                  saving={savingAxis === 'vendor_claim'}
                  onCommit={() => commitAxis('vendor_claim')}
                />
                <AxisCommitRow
                  axis="avero_verification"
                  label="Save Avero verification"
                  hasDraft={draftAvero !== null}
                  saving={savingAxis === 'avero_verification'}
                  onCommit={() => commitAxis('avero_verification')}
                />

                <VerificationHistory detail={data} />
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
            <div className="text-meta text-ink-500">
              {hasUnsavedDraft
                ? 'Un-saved changes — pick each axis and save independently.'
                : 'Every PATCH is audit-logged with actor + before/after.'}
            </div>
            <Dialog.Close
              className={cn(
                'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
              )}
            >
              Close
            </Dialog.Close>
          </div>

          <Dialog.Close
            aria-label="Close verify modal"
            className={cn(
              'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
              'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function AxisCard<T extends string>({
  title,
  badge,
  note,
  persisted,
  draft,
  options,
  onPick,
  saving,
}: {
  title: string;
  badge: 'V' | 'A';
  note: string;
  persisted: T;
  draft: T | null;
  options: { value: T; label: string; icon: typeof Check }[];
  onPick: (next: T) => void;
  saving: boolean;
}) {
  const effective = draft ?? persisted;
  return (
    <section
      className={cn(
        'rounded-card border bg-paper-0 p-4',
        draft !== null ? 'border-accent-amber-600' : 'border-ink-100',
      )}
    >
      <div className="flex items-baseline gap-2">
        <span
          aria-hidden="true"
          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-ink-100 text-meta font-semibold text-ink-700"
        >
          {badge}
        </span>
        <div className="text-h3 font-semibold text-ink-900">{title}</div>
        {draft !== null ? (
          <span className="ml-auto text-meta text-accent-amber-700">unsaved</span>
        ) : null}
      </div>
      <div className="mt-1 text-meta text-ink-500">{note}</div>

      <div className="mt-3 text-meta uppercase tracking-wide text-ink-500">Current</div>
      <div className="mt-1 font-mono text-body-sm text-ink-900">{persisted}</div>

      <div className="mt-3 text-meta uppercase tracking-wide text-ink-500">Pick new value</div>
      <div className="mt-1 flex flex-wrap gap-2">
        {options.map((opt) => {
          const Icon = opt.icon;
          const selected = effective === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={saving}
              onClick={() => onPick(opt.value)}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-card border px-3 text-body-sm font-medium transition-colors',
                selected
                  ? 'border-ink-900 bg-ink-900 text-paper-0'
                  : 'border-ink-200 bg-paper-0 text-ink-700 hover:bg-paper-50',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
              )}
              aria-pressed={selected}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function RollupPreview({
  current,
  previewed,
  staged,
}: {
  current: RollupColor;
  previewed: RollupColor | null;
  staged: boolean;
}) {
  const showPreview = staged && previewed !== null && previewed !== current;
  return (
    <section className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-ink-100 bg-paper-50 px-4 py-3">
      <div>
        <div className="text-meta uppercase tracking-wide text-ink-500">Rollup</div>
        <div className="mt-0.5 text-body-sm text-ink-900">
          {showPreview
            ? `${ROLLUP_LABELS[current]} → ${ROLLUP_LABELS[previewed!]}`
            : ROLLUP_LABELS[current]}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <PostureChip status={rollupToPosture(current)}>{ROLLUP_LABELS[current]}</PostureChip>
        {showPreview ? (
          <>
            <span aria-hidden="true" className="text-ink-300">
              →
            </span>
            <PostureChip status={rollupToPosture(previewed!)}>
              {ROLLUP_LABELS[previewed!]}
            </PostureChip>
          </>
        ) : null}
      </div>
    </section>
  );
}

function rollupToPosture(c: RollupColor): PostureStatus {
  if (c === 'green') return 'healthy';
  if (c === 'yellow_divergent') return 'at-risk';
  if (c === 'red') return 'critical';
  return 'unassessed';
}

function AxisCommitRow({
  axis,
  label,
  hasDraft,
  saving,
  onCommit,
}: {
  axis: VerificationAxis;
  label: string;
  hasDraft: boolean;
  saving: boolean;
  onCommit: () => void;
}) {
  if (!hasDraft) return null;
  return (
    <div className="flex items-center justify-between gap-3 rounded-card border border-accent-amber-600 bg-accent-amber-50 px-3 py-2">
      <div className="text-body-sm text-ink-900">
        <span className="font-mono text-meta text-ink-500">{axis}</span> · ready to save
      </div>
      <button
        type="button"
        onClick={onCommit}
        disabled={saving}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-card bg-ink-900 px-3 text-body-sm font-medium text-paper-0',
          'hover:bg-ink-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        {saving ? <Spinner /> : <Check className="h-3.5 w-3.5" aria-hidden="true" />}
        <span>{label}</span>
      </button>
    </div>
  );
}

function VerificationHistory({ detail }: { detail: VerificationDetail }) {
  if (detail.history.length === 0) {
    return (
      <section className="rounded-card border border-ink-100 bg-paper-0 p-4">
        <div className="text-h3 font-semibold text-ink-900">History</div>
        <p className="mt-2 text-body-sm text-ink-500">No transitions yet.</p>
      </section>
    );
  }
  return (
    <section className="rounded-card border border-ink-100 bg-paper-0 p-4">
      <div className="text-h3 font-semibold text-ink-900">Transition history</div>
      <ol className="mt-2 divide-y divide-ink-100">
        {detail.history.map((t) => (
          <li key={t.id} className="flex flex-wrap items-baseline gap-2 py-2 text-body-sm">
            <span className="font-mono text-meta text-ink-500">{t.axis}</span>
            <span className="font-mono text-ink-700">{t.from_value}</span>
            <span aria-hidden="true" className="text-ink-300">
              →
            </span>
            <span className="font-mono text-ink-900">{t.to_value}</span>
            <span className="ml-auto text-meta text-ink-500">{t.transitioned_at}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}
